import merge from 'lodash/merge'
import mergeWith from 'lodash/mergeWith'
import { Dispatch } from 'redux'
import { ThunkAction } from 'redux-thunk'

import config from 'admin/config/main'
import { reduxDependenciesType } from 'admin/redux/create'
import { State } from 'admin/redux/ducks'
import {
  PersonAdminInfoType,
  OrgAccountStatus,
  OrgAccountType,
} from 'admin/types'
import { AdminApiInterface } from 'admin/types/api'
import createApiReducer, {
  requestDataFor,
  receiveDataFor,
  resetDataFor,
  handleErrorFor,
  clearData,
} from 'redux/utils/createApiDuck'
import {
  AdminTypes,
  AlertType,
  AlertLevels,
  ErrorType,
  APIState,
  LoginType,
  PartialType,
  LoginOrganizationType,
  LoginOrgAccountType,
} from 'types'
import { AuthUserParamObject, DecodedTokenType } from 'types/api'

const moduleName = `${config.appName  }/login`

export default createApiReducer(moduleName)

export function getLoginUrlAction(clientId: string) {
  return (dispatch, getState, { api }) => {
    return api.getLoginUrl(clientId)
  }
}

function convertUserInfo(userInfo: DecodedTokenType): Promise<LoginType> {
  return new Promise<LoginType>((resolve, reject) => {
    const { realm_access, kim, name, given_name, family_name, userType } = userInfo

    // Check required roles for login
    let role
    let inkopBackofficeManager
    if (kim && Array.isArray(kim.engagements)) {
      if (kim.engagements.includes('INKOP_CUSTOMER_ADMIN')) {
        role = 'INKOP_CUSTOMER_ADMIN'
      }
    } else if (realm_access && Array.isArray(realm_access.roles)) {
      if (realm_access.roles.includes('INKOP_ADMIN_USER')) {
        role = 'INKOP_ADMIN_USER'
      }
      inkopBackofficeManager = realm_access.roles.includes('INKOP_HANDLAGGARE')
    }
    if (!role) {
      reject({
        level: AlertLevels.ERROR,
        message: 'PERSON_NOT_ADMIN',
      })
    }
    const result = {
      isAuthenticated: true,
      isLoggedIn: false,
      administratorType: role === 'INKOP_ADMIN_USER' ? AdminTypes.MMB_ADMIN : undefined,
      inkopBackofficeManager,
      kimId: kim && kim.kimCustomerId ? parseInt(kim.kimCustomerId, 10) : null,
      name,
      firstName: given_name,
      lastName: family_name,
      userType,
      orgNumber: null,
      orgKimId: null,
      orgAccountId: null,
      orgName: null,
      orgAccountName: null,
    } as unknown as LoginType
    resolve(result)
  })
}

export function authenticateUserAction(
  authUserParams: AuthUserParamObject,
): ThunkAction<Promise<LoginType | ErrorType>, State, reduxDependenciesType> {
  return (
    dispatch: Dispatch<any,any>,
    getState,
    { api }: reduxDependenciesType,
  ) => {
    dispatch(requestDataFor(moduleName)())
    return api
      .authenticateUser(authUserParams)
      .then(userInfo => {
        return convertUserInfo(userInfo)
      })
      .then(data => {
        dispatch(receiveDataFor(moduleName)(data))
        return data
      })
      .catch(error => {
        dispatch(handleErrorFor(moduleName)(error))
        return Promise.reject(error)
      })
  }
}

export function setLoginDataAction(
  newLoginData: PartialType<LoginType> = {},
): ThunkAction<Promise<LoginType | ErrorType>, State, reduxDependenciesType> {
  return async (
    dispatch: Dispatch<any, State>,
    getState,
    { api }: reduxDependenciesType
  ) => {
    dispatch(requestDataFor(moduleName)())
    const { login } = getState()
    if (login && login.data) {
      const {
        loginUrl,
        kimId,
        administratorType,
        orgKimId,
        orgNumber,
        orgAccountId,
      } = newLoginData
      // Merge non-null values
      let updatedLoginData: LoginType = mergeWith(
        {},
        login.data,
        {
          loginUrl,
          kimId,
          administratorType,
          orgKimId,
          orgNumber,
          orgAccountId,
        },
        (a, b) => (b === null ? a : undefined)
      )

      // Available organizations & accounts
      let organizations
      try {
        organizations = await getLoginOrganizations(updatedLoginData, api)
      } catch {
        organizations = []
      }
      updatedLoginData = {
        ...updatedLoginData,
        organizations,
      }

      // Current account
      const currentOrgAccount = getCurrentOrgAccount(updatedLoginData)
      updatedLoginData = {
        ...updatedLoginData,
        ...currentOrgAccount,
      }

      dispatch(receiveDataFor(moduleName)(updatedLoginData))
      return updatedLoginData
    }
    dispatch(handleErrorFor(moduleName)('NOT_LOGGED_IN'))
    return Promise.reject(new Error('NOT_LOGGED_IN'))
  }
}

/**
 * Get available organizations and accounts for a login
 */
export function getLoginOrganizations(
  loginData: LoginType,
  api: AdminApiInterface,
): Promise<LoginOrganizationType[]> {
  // Any admin with set kimId
  if (loginData.kimId) {
    return api
      .getPersonMemberInfo(loginData.kimId)
      .then((personAdminInfo: PersonAdminInfoType) => {
        let organizations: LoginOrganizationType[]
        organizations = personAdminInfo.organizations.map(org => {
          const { orgNumber, orgName, orgAdmin, orgAccounts, orgKimId } = org
          return {
            orgNumber,
            orgName,
            orgKimId,
            // MMB Admin stays, otherwise set to admin's specific admin level
            administratorType:
              loginData.administratorType === AdminTypes.MMB_ADMIN
                ? AdminTypes.MMB_ADMIN
                : orgAdmin
                ? AdminTypes.ORG_ADMIN
                : AdminTypes.ACCOUNT_ADMIN,
            orgAccounts: orgAccounts.map(orgAccount => {
              return { ...orgAccount }
            }),
          }
        })
        return organizations
      })

      // getOrgAccountsByOrgKimCustomerId: (
      //   orgKimId: number,
      //   status?: OrgAccountStatus | OrgAccountStatus[]
      // ) => Promise<OrgAccountType[] | ErrorType>
    // MBBAdmin without set kimId has access to all accounts for one organization or one account
  } if (loginData.administratorType === AdminTypes.MMB_ADMIN) {
    if (loginData.orgKimId) {
      return api
        .getOrgAccountsByOrgKimCustomerId(
          loginData.orgKimId,
          OrgAccountStatus.ACTIVE,
        )
        .then(orgAccounts => {
          const organizations: LoginOrganizationType[] = []
          if (
            orgAccounts &&
            isOrgAccountsList(orgAccounts) &&
            orgAccounts.length > 0
          ) {
            const { orgNumber, orgName,orgKimId } = orgAccounts[0] as OrgAccountType
            organizations.push({
              orgNumber,
              orgName,
              orgKimId,
              administratorType: AdminTypes.MMB_ADMIN,
              orgAccounts: orgAccounts.map(
                ({ orgAccountId, orgAccountName }) => {
                  return { orgAccountId, orgAccountName }
                },
              ),
            })
          }
          return organizations
        })
    } if (loginData.orgAccountId) {
      return api.getOrgAccount(loginData.orgAccountId).then(orgAccount => {
        const organizations: LoginOrganizationType[] = []
        const {
          orgNumber,
          orgName,
          orgAccountId,
          orgAccountName,
          orgKimId,
        } = orgAccount as OrgAccountType
        organizations.push({
          orgNumber,
          orgKimId,
          orgName,
          administratorType: AdminTypes.MMB_ADMIN,
          orgAccounts: [
            {
              orgAccountId,
              orgAccountName,
            },
          ],
        })
        return organizations
      })
    }
  }
  return Promise.resolve([])
}

/**
 * Set up current selected account data, if available
 */
function getCurrentOrgAccount(
  loginData: PartialType<LoginType> = {},
): PartialType<LoginType> {
  if (
    loginData.orgAccountId &&
    loginData.organizations &&
    loginData.organizations.length > 0
  ) {
    // Set current org account from available ones
    let currentOrganization: LoginOrganizationType
    let currentOrgAccount: LoginOrgAccountType
    for (currentOrganization of loginData.organizations) {
      currentOrgAccount = currentOrganization.orgAccounts.find(
        ({ orgAccountId }) => orgAccountId === loginData.orgAccountId,
      )
      if (currentOrgAccount) break
    }
    if (currentOrgAccount) {
      loginData = merge({}, loginData, {
        orgNumber: currentOrganization.orgNumber,
        orgName: currentOrganization.orgName,
        orgAccountId: currentOrgAccount.orgAccountId,
        orgAccountName: currentOrgAccount.orgAccountName,
        orgKimId: currentOrganization.orgKimId,
      } as LoginType)
      return loginData
    }
  }
  return {}
}

/**
 * Check if required login data is set and set logged in flag
 */
export function loginUserAction(): ThunkAction<
  LoginType | ErrorType,
  State,
  reduxDependenciesType
> {
  return (dispatch: Dispatch<any,any>, getState) => {
    const { login } = getState()
    if (
      login &&
      login.data &&
      login.data.isAuthenticated &&
      login.data.orgAccountId
    ) {
      const loginData: LoginType = merge({}, login.data, {
        isLoggedIn: true,
      }) as LoginType
      dispatch(receiveDataFor(moduleName)(loginData))
      return loginData
    }
    dispatch(
      handleErrorFor(moduleName)('LOGIN_ERROR', {
        isAuthenticated: false,
        isLoggedIn: false,
      })
    )
    throw Error('LOGIN_ERROR')
  }
}

export function logoutUserAction(redirectUri?: string) {
  return async (
    dispatch: Dispatch<any, State>,
    getState,
    { api }: reduxDependenciesType
  ) => {
    // Reset all states
    dispatch(clearData())
    return api.logout(redirectUri)
  }
}

export function resetUserAction() {
  return (dispatch: Dispatch<any, State>) => {
    dispatch(
      resetDataFor(moduleName)({
        isAuthenticated: false,
        isLoggedIn: false,
      })
    )
  }
}

export function updateLoginAction(data) {
  return (dispatch: Dispatch<any, State>) => {
    dispatch(receiveDataFor(moduleName)(data))
  }
}

export function updateLoginOrganizationsAction() {
  return async (
    dispatch: Dispatch<any,State>,
    getState,
    { api }: reduxDependenciesType,
  ) => {
    const {
      login: { data },
    } = getState()
    const organizations = await getLoginOrganizations(data, api)
    dispatch(
      receiveDataFor(moduleName)({ ...data, organizations}),
    )
    return organizations
  }
}

export function selectCurrentOrgAccountAction(
  orgAccountId: string,
): ThunkAction<any, State, reduxDependenciesType> {
  return (dispatch, getState, { api }) => {
    const { login } = getState()
    let error: AlertType
    if (login.data && login.data.organizations) {
      for (const organization of login.data.organizations) {
        for (const orgAccount of organization.orgAccounts) {
          if (orgAccount.orgAccountId === orgAccountId) {
            const loginData: LoginType = { ...login.data, orgNumber: organization.orgNumber, 
              orgName: organization.orgName,
              orgAccountId,
              orgAccountName: orgAccount.orgAccountName,
              orgKimId: organization.orgKimId
            }
            dispatch(receiveDataFor(moduleName)(loginData))
            return loginData
          }
        }
      }
      error = {
        level: AlertLevels.ERROR,
        message: 'ORG_ACCOUNT_NOT_FOUND',
      }
    } else {
      error = {
        level: AlertLevels.ERROR,
        message: 'NOT_LOGGED_IN',
      }
    }
    // dispatch(handleErrorFor(moduleName)(error))
    throw Error(error.message as string)
  }
}

function isOrgAccountsList(data: any): data is OrgAccountType[] {
  return Array.isArray(data)
}

export interface LoginState extends APIState {
  data: LoginType
}
